import { z } from 'zod';
/** BDG-03c. LANDSCAPE is the default: the ordinary shirt-worn badge. */
export const BADGE_ORIENTATIONS = ['LANDSCAPE', 'PORTRAIT'];
/** Pack order inside the sidebar. Not user-reorderable (BDG-06). */
export const BADGE_ZONE_KEYS = ['logo', 'photo', 'qr'];
/** Render order, top to bottom. Not user-reorderable (BDG-06). */
export const BADGE_TEXT_SLOTS = ['HERO', 'SUBHEAD', 'DETAIL1', 'DETAIL2', 'DETAIL3'];
/** What an admin may put in a slot (BDG-03a). Zones are NOT in this list. */
export const BADGE_SLOT_FIELDS = [
    'VISITOR_NAME',
    'VISITOR_COMPANY',
    'HOST_NAME',
    'VISITOR_TYPE',
    'LOCATION_NAME',
    'CHECKED_IN_AT',
    'EXPIRES_AT',
    'CUSTOM_LINE',
];
export const BADGE_FONT_WEIGHTS = ['REGULAR', 'MEDIUM', 'BOLD'];
/**
 * SIDEBAR   — packs vertically inside the sidebar, in BADGE_ZONE_KEYS order.
 * FLOW_END  — renders after the last non-collapsed slot, horizontally centred.
 */
export const BADGE_ZONE_PLACEMENTS = ['SIDEBAR', 'FLOW_END'];
/** How tall one slot's INK is — caption, caption gap and value line box — excluding the gap
 *  to the next slot. The single definition both the fit check and the display-list builder
 *  measure with, so a caption can never be counted in one and forgotten in the other. */
export function badgeSlotHeightDots(slot) {
    const label = slot.label;
    return (label === null ? 0 : label.lineHeightDots + label.spacingBelowDots) + slot.lineHeightDots;
}
/** How far the NEXT slot's origin sits below this one's: its own height plus its trailing gap. */
export function badgeSlotAdvanceDots(slot) {
    return badgeSlotHeightDots(slot) + slot.spacingBelowDots;
}
/** ABOVE — the sidebar is a band across the top. LEFT — it is a fixed-width left column. */
export const BADGE_SIDEBAR_PLACEMENTS = ['ABOVE', 'LEFT'];
/** The caption geometry each labelled slot in a preset shares. Hoisted so a caption cannot
 *  drift between SUBHEAD and DETAIL2 — one caption size per orientation, never per slot. */
const LANDSCAPE_SLOT_LABEL = {
    fontSizeDots: 26,
    lineHeightDots: 28,
    spacingBelowDots: 0,
    letterSpacingDots: 3,
    weight: 'REGULAR',
};
const PORTRAIT_SLOT_LABEL = {
    fontSizeDots: 22,
    lineHeightDots: 24,
    spacingBelowDots: 0,
    letterSpacingDots: 2,
    weight: 'REGULAR',
};
/**
 * The exact dot geometry per orientation, at 300 dpi. The canvases, paddings and zone boxes
 * come verbatim from docs/plans/badge-printing.md §"Orientation, zones and slots" — do not
 * "correct" those. The SLOT type scale and the caption geometry are owned here, and the
 * reasoning behind every one of their numbers is below; do not "correct" those either
 * without redoing the arithmetic.
 *
 * THE HEIGHT BUDGET IS THE CONSTRAINT, and PORTRAIT is where it binds. Its worst case — all
 * five slots assigned, all three zones on — must satisfy `badgeLayoutFits`, and a golden
 * fixture (`004-portrait-full`) plus a contracts unit test both pin that. When the detail
 * values grew to SUBHEAD size and four captions were added above them, that case needed 120
 * dots it did not have, so three levers were pulled, in the order the layout doc prescribes:
 *   1. `blockSpacingDots` 24 -> 18 in PORTRAIT ONLY (it frees the gap ABOVE the stack and the
 *      gap below it twice over: 18 dots of stack budget). LANDSCAPE keeps 24 — its sidebar
 *      packs logo + photo + qr to exactly 632 dots, its full available extent, so shrinking
 *      that gap there would only add slack nothing needs.
 *   2. Line-height ratios tightened from ~1.20 to ~1.11 of the font size. `lineHeightDots`
 *      only positions the next line; it never clips a glyph, and every slot is single-line
 *      (rule 5 truncates, never wraps), so a tighter ratio costs leading and nothing else.
 *   3. `spacingBelowDots` reduced. The caption above the NEXT slot now supplies the visual
 *      break that the gap used to, so the gap can be smaller without the stack reading as
 *      one run-on block.
 * FONT SIZES WERE NOT REDUCED to make it fit. If a future zone or slot pushes PORTRAIT over
 * again, report the numbers rather than shaving the type — see the arithmetic in
 * `badge-layout.test.ts` "fits with every slot filled and every zone on".
 */
export const BADGE_PRESET_GEOMETRY = {
    LANDSCAPE: {
        orientation: 'LANDSCAPE',
        canvasWidthDots: 1109,
        canvasHeightDots: 696,
        paddingDots: 32,
        blockSpacingDots: 24,
        sidebarPlacement: 'LEFT',
        sidebarWidthDots: 300,
        zones: [
            { zone: 'logo', placement: 'SIDEBAR', widthDots: 300, heightDots: 100 },
            { zone: 'photo', placement: 'SIDEBAR', widthDots: 300, heightDots: 300 },
            { zone: 'qr', placement: 'SIDEBAR', widthDots: 184, heightDots: 184 },
        ],
        slots: [
            { slot: 'HERO', fontSizeDots: 88, lineHeightDots: 98, spacingBelowDots: 14, weight: 'BOLD', label: null },
            { slot: 'SUBHEAD', fontSizeDots: 52, lineHeightDots: 58, spacingBelowDots: 14, weight: 'MEDIUM', label: LANDSCAPE_SLOT_LABEL },
            { slot: 'DETAIL1', fontSizeDots: 52, lineHeightDots: 58, spacingBelowDots: 12, weight: 'REGULAR', label: LANDSCAPE_SLOT_LABEL },
            { slot: 'DETAIL2', fontSizeDots: 52, lineHeightDots: 58, spacingBelowDots: 12, weight: 'REGULAR', label: LANDSCAPE_SLOT_LABEL },
            { slot: 'DETAIL3', fontSizeDots: 52, lineHeightDots: 58, spacingBelowDots: 12, weight: 'REGULAR', label: LANDSCAPE_SLOT_LABEL },
        ],
    },
    PORTRAIT: {
        orientation: 'PORTRAIT',
        canvasWidthDots: 696,
        canvasHeightDots: 1109,
        paddingDots: 24,
        // 18, not LANDSCAPE's 24 — lever 1 of the height budget above. PORTRAIT stacks its
        // sidebar ABOVE the text and rides a FLOW_END qr below it, so this gap is spent three
        // times over in the one orientation that has no room to spare.
        blockSpacingDots: 18,
        sidebarPlacement: 'ABOVE',
        sidebarWidthDots: 0,
        zones: [
            { zone: 'logo', placement: 'SIDEBAR', widthDots: 648, heightDots: 110 },
            { zone: 'photo', placement: 'SIDEBAR', widthDots: 300, heightDots: 300 },
            { zone: 'qr', placement: 'FLOW_END', widthDots: 180, heightDots: 180 },
        ],
        slots: [
            { slot: 'HERO', fontSizeDots: 72, lineHeightDots: 80, spacingBelowDots: 12, weight: 'BOLD', label: null },
            { slot: 'SUBHEAD', fontSizeDots: 44, lineHeightDots: 48, spacingBelowDots: 10, weight: 'MEDIUM', label: PORTRAIT_SLOT_LABEL },
            { slot: 'DETAIL1', fontSizeDots: 44, lineHeightDots: 48, spacingBelowDots: 8, weight: 'REGULAR', label: PORTRAIT_SLOT_LABEL },
            { slot: 'DETAIL2', fontSizeDots: 44, lineHeightDots: 48, spacingBelowDots: 8, weight: 'REGULAR', label: PORTRAIT_SLOT_LABEL },
            { slot: 'DETAIL3', fontSizeDots: 44, lineHeightDots: 48, spacingBelowDots: 8, weight: 'REGULAR', label: PORTRAIT_SLOT_LABEL },
        ],
    },
};
export const badgeSlotAssignmentsSchema = z
    .object({
    HERO: z.enum(BADGE_SLOT_FIELDS).nullable(),
    SUBHEAD: z.enum(BADGE_SLOT_FIELDS).nullable(),
    DETAIL1: z.enum(BADGE_SLOT_FIELDS).nullable(),
    DETAIL2: z.enum(BADGE_SLOT_FIELDS).nullable(),
    DETAIL3: z.enum(BADGE_SLOT_FIELDS).nullable(),
})
    .strict();
/**
 * A stored layout (BDG-09 — `.strict()` at every level). No `ZONE_UNSUPPORTED_BY_PRESET` or
 * `SLOT_UNSUPPORTED_BY_PRESET` check: both orientations offer all three zones and all five
 * slots (BDG-03c), so there is no per-preset support policy to enforce here.
 */
export const badgeLayoutSchema = z
    .object({
    orientation: z.enum(BADGE_ORIENTATIONS),
    zones: z.object({ logo: z.boolean(), photo: z.boolean(), qr: z.boolean() }).strict(),
    slots: badgeSlotAssignmentsSchema,
})
    .strict()
    .superRefine((value, ctx) => {
    // A field may fill at most one slot (BDG-03a). Walk BADGE_TEXT_SLOTS in order; the
    // FIRST slot claims the field and any LATER slot with the same field raises exactly
    // one issue at ['slots', <that later slot>] with message 'FIELD_ASSIGNED_TWICE'.
    const claimedBy = new Map();
    for (const slot of BADGE_TEXT_SLOTS) {
        const field = value.slots[slot];
        if (field === null)
            continue;
        if (claimedBy.has(field)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['slots', slot], message: 'FIELD_ASSIGNED_TWICE' });
        }
        else {
            claimedBy.set(field, slot);
        }
    }
});
/**
 * Implements docs/plans/badge-printing.md §"The layout algorithm" rules 1-6. Rule 7
 * (print-time reorientation) is not this function's business — see the "Out of scope"
 * note in S35-TASK-009: no function here may accept, return or reason about a
 * reoriented buffer.
 */
export function badgeLayoutFits(layout) {
    const g = BADGE_PRESET_GEOMETRY[layout.orientation];
    // --- sidebar: zones pack vertically, in BADGE_ZONE_KEYS order, skipping disabled ones.
    const sidebarZones = g.zones.filter((z) => z.placement === 'SIDEBAR' && layout.zones[z.zone] === true);
    const sidebarExtentDots = sidebarZones.reduce((sum, z) => sum + z.heightDots, 0) +
        g.blockSpacingDots * Math.max(0, sidebarZones.length - 1);
    // --- the slot stack's available box
    let availableStackHeightDots;
    let availableSidebarExtentDots;
    if (g.sidebarPlacement === 'ABOVE') {
        const stackTop = g.paddingDots + sidebarExtentDots + (sidebarExtentDots > 0 ? g.blockSpacingDots : 0);
        availableStackHeightDots = g.canvasHeightDots - g.paddingDots - stackTop;
        availableSidebarExtentDots = g.canvasHeightDots - 2 * g.paddingDots;
    }
    else {
        // 'LEFT'
        availableStackHeightDots = g.canvasHeightDots - 2 * g.paddingDots;
        availableSidebarExtentDots = g.canvasHeightDots - 2 * g.paddingDots;
    }
    // --- the slot stack. `badgeSlotAdvanceDots` counts each slot's field-name caption, so a
    // caption can never be drawn by the builder and missed by this check.
    const filled = g.slots.filter((s) => layout.slots[s.slot] !== null);
    const stackHeight = filled.reduce((sum, s) => sum + badgeSlotAdvanceDots(s), 0) -
        (filled.length > 0 ? filled[filled.length - 1].spacingBelowDots : 0);
    // --- a FLOW_END zone rides below the stack
    const flowEndZone = g.zones.find((z) => z.placement === 'FLOW_END' && layout.zones[z.zone] === true);
    const flowHeight = flowEndZone !== undefined ? g.blockSpacingDots + flowEndZone.heightDots : 0;
    const slotStackHeightDots = stackHeight + flowHeight;
    return {
        fits: slotStackHeightDots <= availableStackHeightDots &&
            sidebarExtentDots <= availableSidebarExtentDots,
        slotStackHeightDots,
        availableStackHeightDots,
        sidebarExtentDots,
        availableSidebarExtentDots,
    };
}
/**
 * Mirrors what the S13 browser badge already shows, so a tenant that configures nothing
 * gets continuity rather than a surprise.
 */
export const BUILT_IN_BADGE_LAYOUT = {
    orientation: 'LANDSCAPE',
    zones: { logo: true, photo: true, qr: false },
    slots: {
        HERO: 'VISITOR_NAME',
        SUBHEAD: 'HOST_NAME',
        DETAIL1: 'VISITOR_COMPANY',
        DETAIL2: 'LOCATION_NAME',
        DETAIL3: 'CHECKED_IN_AT',
    },
};
//# sourceMappingURL=badge-layout.js.map